import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  uploadSCGDocument,
  searchSCGRules,
  getSCGSources,
  deleteSCGRule,
  deleteSCGRulesBulk,
  deleteSCGRulesBySource,
  simulateRuleDeprecation,
  getConflictStats,
  getRuleTrustScoresBulk,
  getSCGPdfUrl,
  getAllRejectedSCGRules,
} from '../services/api';
import FileUpload from '../components/FileUpload';
import SecurityShieldLoader from '../components/SecretSquirrelLoader';
import SCGCitation from '../components/SCGCitation';
import EditSCGRuleModal from '../components/EditSCGRuleModal';
import RejectedRulesReview from '../components/RejectedRulesReview';
import SCGConflictsPanel from '../components/SCGConflictsPanel';
import BulkSCGUploader from '../components/bulk_scg/BulkSCGUploader';
import BulkSCGJobsList from '../components/bulk_scg/BulkSCGJobsList';
import TrustScoreBadge from '../components/TrustScoreBadge';
import ViewInPdfButton from '../components/ViewInPdfButton';
import { 
  CogIcon, 
  DocumentIcon, 
  CheckCircleIcon, 
  ExclamationCircleIcon, 
  ClockIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  TrashIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
  EyeSlashIcon,
  InformationCircleIcon,
  PencilIcon,
  DocumentMagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const ConfirmationDialog = ({ isOpen, onClose, onConfirm, title, message, type = 'warning' }) => {
  if (!isOpen) return null;

  const iconColor = type === 'danger' ? 'text-red-400' : 'text-yellow-400';
  const buttonColor = type === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-600 hover:bg-yellow-700';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3 text-center">
          <ExclamationTriangleIcon className={`mx-auto mb-4 w-12 h-12 ${iconColor}`} />
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <div className="mt-2 px-7 py-3">
            <p className="text-sm text-gray-500">{message}</p>
          </div>
          <div className="flex justify-center space-x-4 px-4 py-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 ${buttonColor} text-white text-base font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-300`}
            >
              {type === 'danger' ? 'Delete' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ManageSCG() {
  const [uploadResult, setUploadResult] = useState(null);
  const [showAllRejected, setShowAllRejected] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Processing...');
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClassification, setSelectedClassification] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [limit, setLimit] = useState(20);
  
  // Multi-select states
  const [selectedRules, setSelectedRules] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Tab state — restored from main's SB9 + bulk SCG integration
  const [activeTab, setActiveTab] = useState('rules');
  const [lastBulkBatchId, setLastBulkBatchId] = useState(null);

  // Conflict count badge for the "Cross-SCG Conflicts" tab
  const { data: allRejectedData } = useQuery({
    queryKey: ['scg-all-rejected'],
    queryFn: () => getAllRejectedSCGRules(),
    staleTime: 60 * 1000,
  });

  const conflictStatsQuery = useQuery({
    queryKey: ['scg-conflict-stats-tab'],
    queryFn: getConflictStats,
    staleTime: 60_000,
    retry: false,
  });
  const conflictBadgeCount = conflictStatsQuery.data?.by_verdict?.real_conflict ?? null;

  // E2: auto-route to "Conflicts" tab on first load if unreviewed conflicts exist.
  // One-shot guard via ref so manual tab switches stick.
  const hasDefaultedTabRef = useRef(false);
  useEffect(() => {
    if (hasDefaultedTabRef.current) return;
    const stats = conflictStatsQuery.data;
    if (!stats) return;
    if ((stats.real_conflicts_unreviewed ?? 0) > 0) {
      setActiveTab('conflicts');
    }
    hasDefaultedTabRef.current = true;
  }, [conflictStatsQuery.data]);

  // Deprecation simulation state
  const [deprecationModalOpen, setDeprecationModalOpen] = useState(false);
  const [deprecationResult, setDeprecationResult] = useState(null);
  const [deprecationLoading, setDeprecationLoading] = useState(false);

  const runDeprecationSimulation = async () => {
    if (selectedRules.size === 0) return;
    setDeprecationLoading(true);
    setDeprecationModalOpen(true);
    try {
      const data = await simulateRuleDeprecation(Array.from(selectedRules));
      setDeprecationResult(data);
    } catch (e) {
      setDeprecationResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setDeprecationLoading(false);
    }
  };
  
  // UI states
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRules, setExpandedRules] = useState(new Set());
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: '', action: null, data: null });
  
  // Edit modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  
  const queryClient = useQueryClient();

  // Fetch SCG rules with search and pagination
  const { data: rulesData, isLoading: rulesLoading, error: rulesError } = useQuery({
    queryKey: ['scg-rules-search', searchQuery, selectedClassification, selectedSource, currentPage, limit],
    queryFn: () => searchSCGRules(searchQuery, selectedClassification, selectedSource, limit, currentPage * limit),
    keepPreviousData: true
  });

  // Fetch SCG sources for filter dropdown
  const { data: sourcesData, isLoading: sourcesLoading, error: sourcesError } = useQuery({
    queryKey: ['scg-sources'],
    queryFn: getSCGSources
  });
  

  const rules = rulesData?.rules || [];
  const totalRules = rulesData?.total || 0;
  const hasMore = rulesData?.has_more || false;

  // Bulk-fetch trust scores for the currently visible rules.
  const visibleRuleIds = useMemo(() => rules.map((r) => r.rule_id).filter(Boolean), [rules]);
  const trustScoresQuery = useQuery({
    queryKey: ['scg-rule-trust-scores', visibleRuleIds],
    queryFn: () => getRuleTrustScoresBulk(visibleRuleIds),
    enabled: visibleRuleIds.length > 0,
    staleTime: 60_000,
    retry: false,
  });
  const trustScoresByRuleId = trustScoresQuery.data?.scores || {};

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: uploadSCGDocument,
    onMutate: () => {
      setUploadResult(null);
      setIsLocalLoading(true);
      setLoadingProgress(10);
      setLoadingMessage('Uploading SCG document...');
      
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 85) {
            clearInterval(progressInterval);
            return 85;
          }
          return prev + Math.random() * 12;
        });
      }, 700);
      
      window.scgUploadProgressInterval = progressInterval;
      
      setTimeout(() => setLoadingMessage('Extracting classification rules...'), 1200);
      setTimeout(() => setLoadingMessage('Parsing security classifications...'), 2500);
      setTimeout(() => setLoadingMessage('Indexing for semantic search...'), 4000);
    },
    onSuccess: (data) => {
      if (window.scgUploadProgressInterval) {
        clearInterval(window.scgUploadProgressInterval);
        window.scgUploadProgressInterval = null;
      }
      
      setLoadingProgress(100);
      setTimeout(() => {
        setUploadResult(data);
        setLoadingProgress(0);
        setLoadingMessage('Processing...');
        setIsLocalLoading(false);
        queryClient.invalidateQueries({ queryKey: ['scg-rules-search'] });
        queryClient.invalidateQueries({ queryKey: ['scg-sources'] });
      }, 2000);
    },
    onError: () => {
      if (window.scgUploadProgressInterval) {
        clearInterval(window.scgUploadProgressInterval);
        window.scgUploadProgressInterval = null;
      }
      
      setLoadingProgress(0);
      setLoadingMessage('Processing...');
      setIsLocalLoading(false);
    }
  });

  // Delete mutations
  const deleteSingleMutation = useMutation({
    mutationFn: deleteSCGRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scg-rules-search'] });
      queryClient.invalidateQueries({ queryKey: ['scg-sources'] });
      setSelectedRules(new Set());
    }
  });

  const deleteBulkMutation = useMutation({
    mutationFn: deleteSCGRulesBulk,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scg-rules-search'] });
      queryClient.invalidateQueries({ queryKey: ['scg-sources'] });
      setSelectedRules(new Set());
      setSelectAll(false);
    }
  });

  const deleteBySourceMutation = useMutation({
    mutationFn: deleteSCGRulesBySource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scg-rules-search'] });
      queryClient.invalidateQueries({ queryKey: ['scg-sources'] });
      setSelectedRules(new Set());
      setSelectAll(false);
    }
  });

  // Handle search changes
  const handleSearch = (value) => {
    setSearchQuery(value);
    setCurrentPage(0);
    setSelectedRules(new Set());
    setSelectAll(false);
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    if (filterType === 'classification') {
      setSelectedClassification(value);
    } else if (filterType === 'source') {
      setSelectedSource(value);
    }
    setCurrentPage(0);
    setSelectedRules(new Set());
    setSelectAll(false);
  };

  // Handle rule expansion
  const toggleRuleExpansion = (ruleId) => {
    const newExpanded = new Set(expandedRules);
    if (newExpanded.has(ruleId)) {
      newExpanded.delete(ruleId);
    } else {
      newExpanded.add(ruleId);
    }
    setExpandedRules(newExpanded);
  };

  const expandAllRules = () => {
    const allRuleIds = new Set(rules.map(rule => rule.rule_id));
    setExpandedRules(allRuleIds);
  };

  const collapseAllRules = () => {
    setExpandedRules(new Set());
  };

  // Handle rule selection
  const handleRuleSelect = (ruleId) => {
    const newSelected = new Set(selectedRules);
    if (newSelected.has(ruleId)) {
      newSelected.delete(ruleId);
    } else {
      newSelected.add(ruleId);
    }
    setSelectedRules(newSelected);
    setSelectAll(newSelected.size === rules.length && rules.length > 0);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRules(new Set());
      setSelectAll(false);
    } else {
      const allRuleIds = new Set(rules.map(rule => rule.rule_id));
      setSelectedRules(allRuleIds);
      setSelectAll(true);
    }
  };

  // Handle delete actions
  const handleDeleteSingle = (ruleId) => {
    setConfirmDialog({
      isOpen: true,
      type: 'danger',
      action: () => {
        deleteSingleMutation.mutate(ruleId);
        setConfirmDialog({ isOpen: false });
      },
      data: { ruleId },
      title: 'Delete SCG Rule',
      message: `Are you sure you want to delete rule "${ruleId}"? This action cannot be undone.`
    });
  };

  const handleDeleteSelected = () => {
    const ruleIds = Array.from(selectedRules);
    setConfirmDialog({
      isOpen: true,
      type: 'danger',
      action: () => {
        deleteBulkMutation.mutate(ruleIds);
        setConfirmDialog({ isOpen: false });
      },
      data: { ruleIds },
      title: 'Delete Selected Rules',
      message: `Are you sure you want to delete ${ruleIds.length} selected rule(s)? This action cannot be undone.`
    });
  };

  const handleDeleteBySource = (source) => {
    setConfirmDialog({
      isOpen: true,
      type: 'danger',
      action: () => {
        deleteBySourceMutation.mutate(source);
        setConfirmDialog({ isOpen: false });
      },
      data: { source },
      title: 'Delete All Rules from Source',
      message: `Are you sure you want to delete all rules from "${source}"? This action cannot be undone.`
    });
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingRule(null);
  };

  const handleSCGUpload = (file) => {
    setUploadResult(null);
    uploadMutation.mutate(file);
  };

  // Get unique classification levels for filter
  const classificationLevels = [...new Set(rules.map(rule => rule.classification_level))].filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto">
      <SecurityShieldLoader 
        isLoading={isLocalLoading || uploadMutation.isLoading} 
        progress={loadingProgress} 
        message={loadingMessage} 
      />
      
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Manage Security Classification Guides</h1>
        <p className="mt-2 text-gray-600">
          Upload and manage Security Classification Guides (SCG) to define document classification rules
        </p>
      </div>

      {/* Tabs (Rules / Bulk / Conflicts) — restored from main's SB9+bulk integration */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setActiveTab('rules')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition ${
              activeTab === 'rules'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Rules Management
          </button>
          <button
            onClick={() => setActiveTab('batch')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition ${
              activeTab === 'batch'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Bulk Upload
          </button>
          <button
            onClick={() => setActiveTab('conflicts')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
              activeTab === 'conflicts'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Cross-SCG Conflicts
            {conflictBadgeCount !== null && conflictBadgeCount > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                {conflictBadgeCount}
              </span>
            )}
          </button>
        </nav>
      </div>

      {activeTab === 'batch' && (
        <div className="space-y-6">
          <BulkSCGUploader onUploaded={(batchId) => setLastBulkBatchId(batchId)} />
          <BulkSCGJobsList highlightBatchId={lastBulkBatchId} />
        </div>
      )}

      {activeTab === 'conflicts' && <SCGConflictsPanel />}

      {activeTab === 'rules' && <>

      {/* Upload Results */}
      {uploadResult && (
        <div className="mb-8 bg-white border border-green-200 rounded-lg p-6 shadow-lg">
          <div className="flex items-center mb-3">
            <CheckCircleIcon className="w-6 h-6 text-green-500 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">🔒 SCG Successfully Processed!</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <span className="text-sm font-medium text-gray-700">File:</span>
              <p className="text-sm text-gray-900">{uploadResult.filename}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Extraction Method:</span>
              <p className="text-sm text-gray-900">{uploadResult.extraction_method}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Rules Found:</span>
              <p className="text-sm text-green-600 font-semibold">{uploadResult.classification_rules_found}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Classification Levels:</span>
              <p className="text-sm text-gray-900">{uploadResult.classification_levels?.length || 0}</p>
            </div>
            {uploadResult.rejected_rules_count > 0 && (
              <div>
                <span className="text-sm font-medium text-gray-700">Needs Review:</span>
                <p className="text-sm text-yellow-600 font-semibold">{uploadResult.rejected_rules_count} rule(s)</p>
              </div>
            )}
          </div>
          
          {uploadResult.rules_preview && uploadResult.rules_preview.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Rules Preview:</h4>
              <div className="space-y-2">
                {uploadResult.rules_preview.map((rule, index) => (
                  <div key={index} className="bg-gray-50 rounded p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{rule.id}</span>
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        {rule.classification_level}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{rule.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rejected Rules Review (fresh upload) */}
      {uploadResult?.rejected_rules?.length > 0 && (
        <RejectedRulesReview
          rejectedRules={uploadResult.rejected_rules}
          sourceFile={uploadResult.filename}
          onClose={() => setUploadResult(prev => prev ? { ...prev, rejected_rules: [] } : null)}
        />
      )}

      {/* Rejected Rules Review (persisted, across all uploads) */}
      {showAllRejected && allRejectedData?.rejected_rules?.length > 0 && (
        <RejectedRulesReview
          rejectedRules={allRejectedData.rejected_rules}
          sourceFile="all uploads"
          onClose={() => setShowAllRejected(false)}
        />
      )}

      {/* Upload SCG */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Upload SCG Document</h2>
            <p className="text-sm text-gray-600">
              Upload PDF, JSON, TXT, or Word (DOCX) files containing Security Classification Guide information.
              The system will extract classification rules and keywords.
            </p>
          </div>
          {allRejectedData?.count > 0 && (
            <button
              onClick={() => setShowAllRejected(true)}
              className="flex-shrink-0 inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200 font-medium"
              title="Rules the extraction validator routed to review, across all uploads"
            >
              <ExclamationTriangleIcon className="w-4 h-4" />
              Rejected rules ({allRejectedData.count})
            </button>
          )}
        </div>
        
        <FileUpload 
          onFileSelect={handleSCGUpload}
          accept=".pdf,.json,.txt,.docx,.doc"
          className="mb-4"
        />
        
        {uploadMutation.isError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <ExclamationCircleIcon className="w-5 h-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  SCG upload failed: {uploadMutation.error?.response?.data?.detail || 'Unknown error'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* SCG Rules Management */}
      <div className="bg-white rounded-lg shadow">
        {/* Header with Search and Controls */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">SCG Rules Management</h2>
            <div className="flex items-center space-x-2">
              <CogIcon className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">
                {totalRules} rules total
              </span>
            </div>
          </div>

          {/* Search Bar and Controls */}
          <div className="flex items-center space-x-4 mb-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search rules by ID, description, or keywords..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Per Page</label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(0);
                }}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <FunnelIcon className="w-4 h-4 mr-2" />
              Filters
              {showFilters ? <ChevronUpIcon className="w-4 h-4 ml-2" /> : <ChevronDownIcon className="w-4 h-4 ml-2" />}
            </button>
          </div>

          {/* Filter Controls */}
          {showFilters && (
            <div className="flex items-center space-x-4 mb-4 p-4 bg-gray-50 rounded-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Classification Level</label>
                <select
                  value={selectedClassification}
                  onChange={(e) => handleFilterChange('classification', e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Levels</option>
                  {classificationLevels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source File</label>
                <select
                  value={selectedSource}
                  onChange={(e) => handleFilterChange('source', e.target.value)}
                  disabled={sourcesLoading}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">
                    {sourcesLoading ? 'Loading sources...' : 
                     sourcesError ? 'Error loading sources' : 
                     'All Sources'}
                  </option>
                  {sourcesData?.sources?.map(source => (
                    <option key={source} value={source}>{source}</option>
                  ))}
                  {!sourcesLoading && !sourcesError && (!sourcesData?.sources || sourcesData.sources.length === 0) && (
                    <option disabled>No sources available</option>
                  )}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSelectedClassification('');
                    setSelectedSource('');
                    setSearchQuery('');
                    setCurrentPage(0);
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}

          {/* Bulk Actions */}
          {selectedRules.size > 0 && (
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-md">
              <span className="text-sm text-blue-700">
                {selectedRules.size} rule(s) selected
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={runDeprecationSimulation}
                  className="flex items-center px-3 py-1 text-sm text-amber-700 hover:text-amber-900 hover:bg-amber-100 rounded"
                  title="Estimate classification impact of deprecating these rules"
                >
                  Simulate Deprecation
                </button>
                <button
                  onClick={handleDeleteSelected}
                  disabled={deleteBulkMutation.isLoading}
                  className="flex items-center px-3 py-1 text-sm text-red-700 hover:text-red-900 hover:bg-red-100 rounded"
                >
                  {deleteBulkMutation.isLoading ? (
                    <ArrowPathIcon className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <TrashIcon className="w-4 h-4 mr-1" />
                  )}
                  Delete Selected
                </button>
              </div>
            </div>
          )}

          {deprecationModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setDeprecationModalOpen(false)}>
              <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-6 border-b border-gray-200 flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold">Deprecation Impact Simulation</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      What happens to past classifications if these rules are removed.
                    </p>
                  </div>
                  <button onClick={() => setDeprecationModalOpen(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                </div>
                <div className="p-6">
                  {deprecationLoading && <div className="text-gray-500">Simulating…</div>}
                  {deprecationResult?.error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded">{deprecationResult.error}</div>
                  )}
                  {deprecationResult && !deprecationResult.error && (
                    <>
                      <div className="grid grid-cols-3 gap-3 mb-6">
                        <div className="bg-gray-50 rounded-md p-3 text-center">
                          <div className="text-2xl font-bold">{deprecationResult.total_decisions_affected}</div>
                          <div className="text-xs text-gray-500 uppercase">Decisions touched</div>
                        </div>
                        <div className="bg-red-50 rounded-md p-3 text-center">
                          <div className="text-2xl font-bold text-red-700">
                            {deprecationResult.unified_breakdown?.decisions_that_would_drop_to_unknown || 0}
                          </div>
                          <div className="text-xs text-gray-500 uppercase">Would lose all citations</div>
                        </div>
                        <div className="bg-green-50 rounded-md p-3 text-center">
                          <div className="text-2xl font-bold text-green-700">
                            {deprecationResult.unified_breakdown?.decisions_with_backup_citations || 0}
                          </div>
                          <div className="text-xs text-gray-500 uppercase">Keep backup citations</div>
                        </div>
                      </div>
                      {deprecationResult.per_rule?.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2">Per-rule impact</h4>
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left">Rule</th>
                                <th className="px-3 py-2">Cited in</th>
                                <th className="px-3 py-2">Would drop</th>
                                <th className="px-3 py-2">Would keep</th>
                              </tr>
                            </thead>
                            <tbody>
                              {deprecationResult.per_rule.map((r) => (
                                <tr key={r.rule_id} className="border-b border-gray-100">
                                  <td className="px-3 py-2 font-mono text-xs">{r.rule_id}</td>
                                  <td className="px-3 py-2 text-center">{r.cited_in}</td>
                                  <td className="px-3 py-2 text-center text-red-700">{r.would_lose_all_citations}</td>
                                  <td className="px-3 py-2 text-center text-green-700">{r.would_keep_citations}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Rules List */}
        <div className="p-6">
          {rulesLoading ? (
            <div className="flex items-center justify-center py-8">
              <ClockIcon className="w-6 h-6 text-gray-400 animate-spin mr-2" />
              <span className="text-gray-600">Loading SCG rules...</span>
            </div>
          ) : rulesError ? (
            <div className="text-center py-8">
              <ExclamationCircleIcon className="w-12 h-12 mx-auto text-red-400 mb-4" />
              <p className="text-red-600 mb-2">Failed to load SCG rules</p>
              <p className="text-sm text-gray-400">{rulesError.message}</p>
            </div>
          ) : rules.length > 0 ? (
            <div className="space-y-4">
              {/* Select All Header and View Controls */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-3 text-sm font-medium text-gray-700">
                    Select All ({rules.length} rules)
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={expandAllRules}
                    className="flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                  >
                    <EyeIcon className="w-4 h-4 mr-1" />
                    Expand All
                  </button>
                  <button
                    onClick={collapseAllRules}
                    className="flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                  >
                    <EyeSlashIcon className="w-4 h-4 mr-1" />
                    Collapse All
                  </button>
                </div>
              </div>

              {/* Rules */}
              {rules.map((rule) => {
                const isExpanded = expandedRules.has(rule.rule_id);
                const hasExtendedInfo = rule.guidance || rule.rationale || rule.examples || rule.related_categories || rule.duration || rule.declassification_date || rule.location_metadata;
                
                return (
                  <div key={rule.rule_id} className="border border-gray-200 rounded-lg hover:bg-gray-50">
                    {/* Main Rule Content */}
                    <div className="p-4">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedRules.has(rule.rule_id)}
                          onChange={() => handleRuleSelect(rule.rule_id)}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <DocumentIcon className="w-5 h-5 text-gray-400" />
                              <SCGCitation
                                ruleId={rule.rule_id}
                                compact={true}
                                showClassificationBadge={false}
                                className="font-medium text-gray-900 hover:text-blue-600"
                              />
                              {(() => {
                                const ts = trustScoresByRuleId[rule.rule_id];
                                if (!ts || ts.cited_count === 0) return null;
                                return (
                                  <TrustScoreBadge
                                    trustScore={ts.trust_score}
                                    citedCount={ts.cited_count}
                                    overrideCount={ts.override_count}
                                    size="sm"
                                  />
                                );
                              })()}
                              {/* Full Classification Badge with Caveats/Releasability */}
                              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                rule.classification_level === 'TOP SECRET' ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' :
                                rule.classification_level === 'SECRET' ? 'bg-red-100 text-red-800 border border-red-300' :
                                rule.classification_level === 'CONFIDENTIAL' ? 'bg-blue-100 text-blue-800 border border-blue-300' :
                                rule.classification_level === 'UNCLASSIFIED' ? 'bg-green-100 text-green-800 border border-green-300' :
                                'bg-gray-100 text-gray-800 border border-gray-300'
                              }`}>
                                {/* Build full classification string */}
                                {rule.classification_level}
                                {rule.caveats && rule.caveats.length > 0 && `/${rule.caveats.join('/')}`}
                                {rule.releasability && rule.releasability.length > 0 && `//${rule.releasability.join('/')}`}
                              </span>
                              {hasExtendedInfo && (
                                <button
                                  onClick={() => toggleRuleExpansion(rule.rule_id)}
                                  className="flex items-center text-xs text-blue-600 hover:text-blue-800"
                                >
                                  {isExpanded ? (
                                    <>
                                      <ChevronUpIcon className="w-4 h-4 mr-1" />
                                      Less Details
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDownIcon className="w-4 h-4 mr-1" />
                                      More Details
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              {rule.source && (
                                rule.source_document?.file_hash ? (
                                  <a
                                    href={getSCGPdfUrl(rule.source_document.file_hash, rule.location_metadata?.page_number || null)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                    title={rule.citation_display?.page_reference || rule.source}
                                  >
                                    {rule.citation_display?.page_reference || `Source: ${rule.source}`}
                                  </a>
                                ) : (
                                  <span className="text-xs text-gray-500">Source: {rule.source}</span>
                                )
                              )}
                              {rule.source_document?.file_hash && (
                                <ViewInPdfButton
                                  ruleId={rule.rule_id || rule.id}
                                  sourceInfo={rule}
                                  variant="icon"
                                  size="sm"
                                />
                              )}
                              <button
                                onClick={() => handleEditRule(rule)}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Edit Rule"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteSingle(rule.rule_id)}
                                disabled={deleteSingleMutation.isLoading}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Delete Rule"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Basic Information */}
                          {(rule.item || rule.description) && (
                            <p className="text-sm text-gray-700 mb-3 leading-relaxed">{rule.item || rule.description}</p>
                          )}
                          
                          {/* Keywords */}
                          {rule.keywords && rule.keywords.length > 0 && (
                            <div className="mb-3">
                              <span className="text-xs font-medium text-gray-700">Keywords:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {rule.keywords.map((keyword, keywordIndex) => (
                                  <span
                                    key={keywordIndex}
                                    className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700"
                                  >
                                    {keyword}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Classification Caveats - Always Visible */}
                          {rule.caveats && rule.caveats.length > 0 && (
                            <div className="mb-3">
                              <span className="text-xs font-medium text-gray-700">Caveats:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {rule.caveats.map((caveat, idx) => (
                                  <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800">
                                    {caveat}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Releasability - Always Visible */}
                          {rule.releasability && rule.releasability.length > 0 && (
                            <div className="mb-3">
                              <span className="text-xs font-medium text-gray-700">Releasability:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {rule.releasability.map((rel, idx) => (
                                  <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                                    {rel}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Basic Reason */}
                          {rule.reason && (
                            <div className="mb-3">
                              <span className="text-xs font-medium text-gray-700">Reason:</span>
                              <p className="text-xs text-gray-600 mt-1">{rule.reason}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Extended Information (Expandable) */}
                    {isExpanded && hasExtendedInfo && (
                      <div className="border-t border-gray-200 bg-gray-50 p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Detailed Guidance */}
                          {rule.guidance && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                <InformationCircleIcon className="w-4 h-4 mr-1 text-blue-500" />
                                Guidance
                              </h4>
                              <p className="text-sm text-gray-700 bg-white p-3 rounded border">{rule.guidance}</p>
                            </div>
                          )}

                          {/* Rationale */}
                          {rule.rationale && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Rationale</h4>
                              <p className="text-sm text-gray-700 bg-white p-3 rounded border">{rule.rationale}</p>
                            </div>
                          )}

                          {/* Examples */}
                          {rule.examples && rule.examples.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Examples</h4>
                              <div className="bg-white p-3 rounded border">
                                {rule.examples.map((example, idx) => (
                                  <div key={idx} className="text-sm text-gray-700 mb-2 last:mb-0">
                                    <span className="text-xs text-gray-500">• </span>
                                    {example}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Related Categories */}
                          {rule.related_categories && rule.related_categories.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Related Categories</h4>
                              <div className="flex flex-wrap gap-1">
                                {rule.related_categories.map((category, idx) => (
                                  <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                                    {category}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}


                          {/* Duration/Time Info */}
                          {(rule.duration || rule.declassification_date) && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Classification Duration</h4>
                              <div className="bg-white p-3 rounded border text-sm text-gray-700">
                                {rule.duration && <div><strong>Duration:</strong> {rule.duration}</div>}
                                {rule.declassification_date && <div><strong>Declassification Date:</strong> {rule.declassification_date}</div>}
                              </div>
                            </div>
                          )}

                          {/* Source Location Info */}
                          {rule.location_metadata && (
                            <div className="md:col-span-2">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Source Location</h4>
                              <div className="bg-white p-3 rounded border text-sm text-gray-600">
                                {rule.location_metadata.page_number && <div><strong>Page:</strong> {rule.location_metadata.page_number}</div>}
                                {rule.source_document?.file_hash && (
                                  <div className="mt-2">
                                    <a
                                      href={getSCGPdfUrl(rule.source_document.file_hash, rule.location_metadata.page_number || null)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                      View Source PDF{rule.location_metadata.page_number ? ` (Page ${rule.location_metadata.page_number})` : ''}
                                    </a>
                                  </div>
                                )}
                                {rule.location_metadata.source_text && (
                                  <div className="mt-2">
                                    <strong>Source Text:</strong>
                                    <p className="mt-1 italic text-gray-600 border-l-2 border-gray-300 pl-3">
                                      "{rule.location_metadata.source_text.substring(0, 200)}{rule.location_metadata.source_text.length > 200 ? '...' : ''}"
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Pagination */}
              <div className="flex items-center justify-between pt-4">
                <div className="text-sm text-gray-700">
                  Showing {currentPage * limit + 1} to {Math.min((currentPage + 1) * limit, totalRules)} of {totalRules} rules
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-700">
                    Page {currentPage + 1}
                  </span>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!hasMore}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <CogIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 mb-2">No SCG rules found</p>
              <p className="text-sm text-gray-400">
                {searchQuery || selectedClassification || selectedSource 
                  ? 'Try adjusting your search criteria'
                  : 'Upload an SCG document to begin defining classification rules'
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Source Management */}
      {sourcesData?.sources && sourcesData.sources.length > 0 && (
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Source File Management</h3>
          <div className="space-y-2">
            {sourcesData.sources.map(source => (
              <div key={source} className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                <span className="text-sm text-gray-900">{source}</span>
                <button
                  onClick={() => handleDeleteBySource(source)}
                  disabled={deleteBySourceMutation.isLoading}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Delete All Rules
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-2">About Security Classification Guides</h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p>
            Security Classification Guides (SCGs) provide specific guidance on classifying information. 
            They contain classification elements that define what information should be classified and at what level.
          </p>
          <p>
            <strong>Supported formats:</strong>
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>PDF files:</strong> The system will extract text and attempt to identify classification rules</li>
            <li><strong>JSON files:</strong> Structured format with predefined classification elements and keywords</li>
            <li><strong>TXT files:</strong> Plain text files with SCG rules (structured table format or free text)</li>
            <li><strong>Word files (DOCX):</strong> Microsoft Word documents containing SCG rules and classification tables</li>
          </ul>
          <p>
            Use the search and filter tools to manage your SCG rules efficiently. You can select multiple rules for bulk operations or delete entire source files.
          </p>
        </div>
      </div>

      </>}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false })}
        onConfirm={confirmDialog.action}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
      />

      {/* Edit SCG Rule Modal */}
      <EditSCGRuleModal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        rule={editingRule}
      />
    </div>
  );
}